import PlatformIntegrationAPI from '../api/PlatformIntegrationAPI';
import {
  PlatformIntegrationEnum,
  PlatformIntegrationEvent,
} from '../types/integrationType';

export const getUtmQueryObject = (): Record<string, string> => {
  if (
    typeof window !== 'undefined' &&
    window.location &&
    window.location.search
  ) {
    const searchParams = new URLSearchParams(window.location.search);
    // Get only UTM parameters from the query string
    const utmKeys = [
      'utm_id',
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
    ];
    const filteredParams: Record<string, string> = {};
    utmKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) {
        filteredParams[key] = value;
      }
    });
    return filteredParams as Record<string, string>;
  }
  return {};
};

export const triggerFacebookPixelIntegrationEvent = async (
  creatorId: string,
  event: PlatformIntegrationEvent,
  payload: Record<string, unknown>,
) => {
  try {
    const utmQueryObject = getUtmQueryObject();
    await PlatformIntegrationAPI.triggerFacebookPixelIntegrationEventAPICall(
      creatorId,
      event,
      {
        ...utmQueryObject,
        ...payload,
        timestamp: new Date().toISOString(),
      },
      PlatformIntegrationEnum.FACEBOOK_CONVERSATION,
    );
  } catch (error) {
    console.log(error);
  }
};

// export const facebookPixel = (
//   fbPixelId: string,
//   eventName: string,
//   elementId: string,
//   queryObject: Record<string, unknown> = {},
// ) => {
//   if (!document) return;
//   const FBImage: HTMLElement | null = document.getElementById(elementId);
//   if (!FBImage || !(FBImage instanceof HTMLImageElement)) return;
//   let src = `https://www.facebook.com/tr`;
//   const utmQueryObject = getUtmQueryObject();
//   const queryParams = objectToQuery({
//     ...queryObject,
//     ...utmQueryObject,
//     id: fbPixelId,
//     ev: eventName,
//     timestamp: new Date().toISOString(),
//   });
//   src = `${src}?${queryParams}`;
//   FBImage.src = src;
//   // alert('Purchase Pageview FB Pixel event');
//   FBImage.onload = () => {
//     FBImage.src = '';
//   };
// };

// Track if GTM is loaded (each user has only one GTM ID)
let isGTMLoaded = false;
let currentGTMId: string | null = null;
let gtmLoadingPromise: Promise<void> | null = null;

/**
 * Initialize dataLayer if it doesn't exist
 */
const initializeDataLayer = (): void => {
  if (typeof window !== 'undefined') {
    const win = window as Window & {
      dataLayer?: Array<Record<string, unknown>>;
    };
    if (!win.dataLayer) {
      win.dataLayer = [];
    }
  }
};

/**
 * Load Google Tag Manager script dynamically (only once per user)
 */
const loadGTMScript = (gtmId: string): Promise<void> => {
  // If already loaded with the same GTM ID, return resolved promise
  if (isGTMLoaded && currentGTMId === gtmId) {
    return Promise.resolve();
  }

  // If already loading, return the existing promise
  if (gtmLoadingPromise) {
    return gtmLoadingPromise;
  }

  // If GTM ID changed (edge case), log warning but proceed
  if (isGTMLoaded && currentGTMId !== gtmId) {
    console.warn(
      `GTM already loaded with ID: ${currentGTMId}. New ID requested: ${gtmId}. Using existing GTM.`,
    );
    return Promise.resolve();
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Window or document is not available'));
  }

  initializeDataLayer();

  // Create GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

  // Create loading promise
  gtmLoadingPromise = new Promise<void>((resolve, reject) => {
    // Handle script load
    script.onload = () => {
      isGTMLoaded = true;
      currentGTMId = gtmId;
      gtmLoadingPromise = null;
      resolve();
    };

    // Handle script error
    script.onerror = () => {
      gtmLoadingPromise = null;
      const error = new Error(`Failed to load GTM script for ID: ${gtmId}`);
      reject(error);
    };
  });

  // Insert script into head
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  return gtmLoadingPromise;
};

/**
 * Push event to dataLayer
 */
const pushEventToDataLayer = (
  eventName: string,
  queryObject: Record<string, unknown>,
): void => {
  if (typeof window === 'undefined') {
    console.warn('Window is not available');
    return;
  }

  const win = window as Window & {
    dataLayer?: Array<Record<string, unknown>>;
  };

  if (!win.dataLayer) {
    console.warn('dataLayer is not available. Initializing...');
    initializeDataLayer();
  }

  const utmQueryObject = getUtmQueryObject();
  const eventData = {
    event: eventName,
    ...queryObject,
    ...utmQueryObject,
    timestamp: new Date().toISOString(),
  } as Record<string, unknown>;

  if (win.dataLayer) {
    win.dataLayer.push(eventData);
  }
};

/**
 * Google Tag Manager function to dynamically load GTM (once) and trigger events (multiple times)
 * Each user has only one GTM ID, but events can be pushed multiple times
 * @param gtmId - Google Tag Manager ID (e.g., 'GTM-XXXXXXX')
 * @param eventName - Event name to trigger (e.g., 'purchase', 'signup')
 * @param queryObject - Additional event data to send with the event
 */
export const googleTagManager = async (
  gtmId: string,
  eventName: string,
  queryObject: Record<string, unknown> = {},
): Promise<void> => {
  try {
    // Validate inputs
    if (!gtmId || typeof gtmId !== 'string') {
      console.error('Invalid GTM ID provided:', gtmId);
      return;
    }

    if (!eventName || typeof eventName !== 'string') {
      console.error('Invalid event name provided:', eventName);
      return;
    }

    // Initialize dataLayer
    initializeDataLayer();

    // Load GTM script only if not already loaded (one-time initialization)
    if (!isGTMLoaded) {
      try {
        await loadGTMScript(gtmId);
      } catch (error) {
        console.error('Error loading GTM script:', error);
        // Even if script fails to load, try to push event to dataLayer
        // GTM might be loaded from another source
      }
    }

    // Push event to dataLayer (can be called multiple times)
    pushEventToDataLayer(eventName, queryObject);
  } catch (error) {
    console.error('Error in googleTagManager:', error);
  }
};
